import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { subMinutes, fromUnixTime } from 'date-fns';
import { format } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import type DeskModel from 'garaje/models/desk';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type ReservationModel from 'garaje/models/reservation';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { ResourceType, AvailabilityStatus } from 'garaje/services/live-map';
import type LiveMapService from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

/**
 * Next desk assignment data from GraphQL search results
 */
interface NextAssignment {
  deskId: string;
  deskName: string;
  startTime: string;
}

interface DeskPopupComponentArgs {
  feature: MapFeatureModel;
  desks: ArrayProxy<DeskModel>;
  reservations: ArrayProxy<ReservationModel>;
  employees: ArrayProxy<EmployeeModel>;
  selectedDate: Date;
  selectedTime?: number;
  selectedEndTime?: number;
  setNeighborhoodToView: (neighborhoodId: string) => void;
  currentUserReservation?: ReservationModel;
  loadReservations?: () => void;
  loadAvailableDesks?: () => void;
  onCancel?: () => void;
  selectedNextAssignment?: NextAssignment;
  availableDesks?: DeskModel[];
  isAtCapacity?: boolean;
}

export default class DeskPopup extends Component<DeskPopupComponentArgs> {
  @service declare state: StateService;
  @service declare store: Store;
  @service declare liveMap: LiveMapService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked showNeighborhoodPopup = false;

  get selectedDateFormatted(): string {
    const { selectedDate } = this.args;
    return format(subMinutes(selectedDate, this.state.minutesBetweenTimezones(selectedDate)), 'MMMM dd, yyyy');
  }

  get floor(): FloorModel | null {
    const desk = this.getDeskById(this.args.feature.externalId);
    if (!desk) return null;

    return this.store.peekRecord('floor', desk.belongsTo('floor').id());
  }

  get neighborhood(): NeighborhoodModel | null {
    const { feature } = this.args;
    const desk = this.getDeskById(feature.externalId);
    if (!desk) return null;

    const neighborhoodId = desk.belongsTo('neighborhood').id();
    if (neighborhoodId) {
      return this.store.peekRecord('neighborhood', neighborhoodId);
    }
    return null;
  }

  @action
  getLenderName(deskId: string): string | null {
    if (!this.featureFlags.isEnabled('reverse-hoteling')) return null;
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState?.lender) return null;
    return deskState.lender.name || 'An employee';
  }

  @action
  getReservationByDeskId(deskId: string): ReservationModel | undefined {
    return this.args.reservations.find((res) => res.belongsTo('desk').id() == deskId);
  }

  @action
  getDeskById(deskId: string): DeskModel | null {
    return this.store.peekRecord('desk', deskId);
  }

  @action
  getEmployeeByEmail(email: string): EmployeeModel | undefined {
    const employees = this.store.peekAll('employee');
    return employees.find((employee) => employee.email === email);
  }

  /**
   * Gets the correct employee for a desk using the liveMap service as the primary source.
   * Falls back to reservation/desk email lookup ONLY when liveMap has no occupant data.
   * When liveMap identifies a specific employee but that employee isn't in the store yet,
   * returns undefined so the template can use getLiveMapProfileData as a fallback.
   */
  @action
  getEmployeeForDesk(deskId: string, reservationEmail?: string, assignedToEmail?: string): EmployeeModel | undefined {
    const employees = this.store.peekAll('employee');

    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (deskState) {
      const employeeId = getIdFromURN(deskState.status?.reservedFor) || getIdFromURN(deskState.assignedTo);
      if (employeeId) {
        const employeeById = employees.find((e) => e.id === employeeId);
        if (employeeById) return employeeById;
      }
    }

    const email = reservationEmail || assignedToEmail;
    if (email) {
      return employees.find((e) => e.email === email);
    }

    return undefined;
  }

  /**
   * Gets profile display data directly from the liveMap service.
   * Used as a fallback when the employee record hasn't been loaded into the store yet.
   */
  @action
  getLiveMapProfileData(deskId: string): { id: string | null; name: string; avatarUrl: string | null } | null {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return null;

    const profileData = deskState.status?.reservedForProfileData || deskState.assignedToProfileData;
    if (!profileData?.name) return null;

    return {
      id: profileData.id,
      name: profileData.name,
      avatarUrl: profileData.avatarUrl,
    };
  }

  @action
  getActiveNeighborhood(deskId: string): NeighborhoodModel | null {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (deskState && 'activeNeighborhoodId' in deskState) {
      // activeNeighborhoodId is present, null means no active neighbourhood today
      if (!deskState.activeNeighborhoodId) return null;
      return this.store.peekRecord('neighborhood', String(deskState.activeNeighborhoodId));
    }
    // activeNeighborhoodId is absent, fall back to static relationship
    const staticNeighborhoodId = this.getDeskById(deskId)?.belongsTo('neighborhood').id();
    if (!staticNeighborhoodId) return null;
    return this.store.peekRecord('neighborhood', staticNeighborhoodId);
  }

  @action
  partialDayFormat(reservation: ReservationModel): string {
    const startTime = fromUnixTime(reservation.startTime);
    const formattedStartTime = format(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), 'h:mma');
    const endTime = fromUnixTime(reservation.endTime);
    const formattedEndTime = format(subMinutes(endTime, this.state.minutesBetweenTimezones(endTime)), 'h:mma');
    return `${formattedStartTime} - ${formattedEndTime}`;
  }

  /**
   * Formats the startTime from nextAssignment (GraphQL).
   * Only shows startTime since endTime is not available from GraphQL yet.
   */
  get nextAssignmentTimeFormat(): string | null {
    const { selectedNextAssignment } = this.args;
    if (!selectedNextAssignment?.startTime) {
      return null;
    }

    const startTime = new Date(selectedNextAssignment.startTime);
    return format(subMinutes(startTime, this.state.minutesBetweenTimezones(startTime)), 'h:mma');
  }

  /**
   * Checks if the popup should use nextAssignment time instead of reservation time.
   * This happens when an employee was selected from search results with nextAssignment data.
   */
  get shouldUseNextAssignmentTime(): boolean {
    return !!this.args.selectedNextAssignment?.startTime;
  }

  @action
  showScheduleSharingDisabled(employee: EmployeeModel, desk: DeskModel): boolean {
    return (
      !(desk.assignedTo === employee.email && employee.belongsTo('user').id() === this.state.currentUser.id) &&
      !(this.args.currentUserReservation?.belongsTo('user').id() === employee.belongsTo('user').id()) &&
      (employee.hideFromEmployeeSchedule || !this.state.currentLocation.employeeScheduleEnabled)
    );
  }

  @action
  onNeighborhoodClick(neighborhoodId: string, e: Event): void {
    e.stopPropagation();
    this.args.setNeighborhoodToView(neighborhoodId);
    this.showNeighborhoodPopup = true;
  }

  @action
  getDeskAvailabilityStatus(deskId: string): AvailabilityStatus | null {
    const desk = this.getDeskById(deskId);
    if (desk && desk.enabled === false) {
      return AvailabilityStatus.UNAVAILABLE;
    }

    const liveMapState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (liveMapState) {
      return liveMapState.status.availabilityStatus;
    }

    // No state for this desk: if the live array has other entries, the BE intentionally
    // omitted this desk (it is available). If the live array is entirely empty, the BE
    // returned no data (e.g. API error for certain date ranges) — treat as unavailable.
    return this.liveMap.resourceStates.length > 0 ? AvailabilityStatus.AVAILABLE : AvailabilityStatus.UNAVAILABLE;
  }

  get isAccessible(): boolean {
    const desk = this.getDeskById(this.args.feature.externalId);

    if (!desk) {
      return false;
    }

    const { availableDesks } = this.args;

    // undefined means the task hasn't run yet or isn't used in this context — default to accessible
    if (availableDesks === undefined) {
      return true;
    }

    // empty array means the task ran but returned nothing — desk is not accessible
    // populated array — desk is accessible only if it appears in the list
    return availableDesks.includes(desk);
  }

  /**
   * Returns true when the live map marks a desk as UNAVAILABLE due to neighbourhood
   * restriction (activeNeighborhoodId is set, no occupant)
   */
  @action
  isNeighborhoodRestricted(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return false;
    return (
      deskState.status?.availabilityStatus === AvailabilityStatus.UNAVAILABLE &&
      !!deskState.activeNeighborhoodId &&
      !deskState.assignedTo &&
      !deskState.status?.reservedFor
    );
  }

  /**
   * Checks if the desk has an occupant (assigned or reserved) from liveMap data,
   * regardless of availabilityStatus. A desk can be "AVAILABLE" in status but still
   * have a schedule-based assignment that should be displayed.
   */
  @action
  hasLiveMapOccupant(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);
    if (!deskState) return false;
    return !!(deskState.assignedTo || deskState.status?.reservedFor);
  }

  /**
   * Checks if the desk has an occupant OTHER than the current user.
   * Returns false if the current user is the occupant (they should see book buttons).
   * Returns true if someone else is the occupant (they should see occupied view).
   *
   * Handles two URN formats:
   * - assignedTo: urn:envoy:employee:email:{email}
   * - reservedFor: urn:envoy:employee:{employee_id}
   */
  @action
  hasOtherUserOccupant(deskId: string): boolean {
    const deskState = this.liveMap.getResourceStateByIdAndType(deskId, ResourceType.DESK);

    if (!deskState) {
      return false;
    }

    const hasOccupant = !!(deskState.assignedTo || deskState.status?.reservedFor);

    if (!hasOccupant) {
      return false;
    }

    // Get the full URN to check format before extraction
    const reservedForUrn = deskState.status?.reservedFor;
    const assignedToUrn = deskState.assignedTo;
    const urnToCheck = reservedForUrn || assignedToUrn;

    if (!urnToCheck) {
      return true;
    }

    // Check if the URN uses the email format: urn:envoy:employee:email:{email}
    if (urnToCheck.includes(':email:')) {
      // Email-based URN format
      const occupantEmail = getIdFromURN(urnToCheck);

      if (occupantEmail === this.currentAdmin.employee?.email) {
        return false; // Current user is the occupant
      }
    } else {
      // ID-based URN format: urn:envoy:employee:{employee_id}
      const occupantId = getIdFromURN(urnToCheck);

      if (occupantId === this.currentAdmin.employee?.id) {
        return false; // Current user is the occupant
      }
    }

    return true; // Someone else is the occupant
  }

  /**
   * Checks if the current desk has a reservation made by the current user
   */
  @action
  isReservedByCurrentUser(reservation: ReservationModel | undefined): boolean {
    if (!reservation) return false;

    const reservationUserId = reservation.belongsTo('user').id();
    return reservationUserId === this.state.currentUser?.id;
  }

  /**
   * Checks if the current desk is assigned to the current user
   */
  @action
  isAssignedToCurrentUser(desk: DeskModel | null): boolean {
    if (!desk) return false;

    return desk.assignedTo === this.state.currentUser?.email;
  }

  /**
   * Determines if the "Free up" button should be shown.
   * Show whenever the current user has a reservation, regardless of assignment status.
   * This allows users to free up their assigned desks if they've reserved them.
   */
  @action
  canFreeUp(reservation: ReservationModel | undefined, _desk: DeskModel | null): boolean {
    return this.isReservedByCurrentUser(reservation);
  }

  /**
   * Gets the employee data for displaying in the popup
   */
  @action
  getEmployeeData(
    employee: EmployeeModel | undefined,
    reservation: ReservationModel | undefined,
  ): { name: string; isCurrentUser: boolean; profilePictureUrl: string | null } | null {
    if (!employee) return null;

    const isCurrentUser = this.isReservedByCurrentUser(reservation);
    return {
      name: employee.name,
      isCurrentUser,
      profilePictureUrl: employee.profilePictureUrl || null,
    };
  }

  /**
   * Task to release/free up a desk reservation
   */
  releaseReservationTask = task({ drop: true }, async (reservation: ReservationModel): Promise<void> => {
    try {
      const url = urlBuilder.rms.releaseDesk();

      await this.ajax.request(url, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: {
          data: {
            attributes: {
              'reservation-id': reservation.id,
            },
          },
        },
      });

      this.store.unloadRecord(reservation);
      this.flashMessages.showAndHideFlash('success', 'Desk freed up successfully');

      // Reload reservations and available desks
      if (this.args.loadReservations) {
        this.args.loadReservations();
      }
      if (this.args.loadAvailableDesks) {
        this.args.loadAvailableDesks();
      }

      // Close the popup
      if (this.args.onCancel) {
        this.args.onCancel();
      }
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Failed to free up desk');
      throw error;
    }
  });
}
